import React, {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  initialWizardState,
  wizardReducer,
  canAdvance,
  stateToSearchParams,
  searchParamsToState,
  StepIndex,
} from "../wizard/ExportWizardState";
import { StepMonths } from "../wizard/StepMonths";
import { StepFilter } from "../wizard/StepFilter";
import { StepSearch } from "../wizard/StepSearch";
import { StepOutput } from "../wizard/StepOutput";
import { StepReview } from "../wizard/StepReview";
import { WizardFunnel } from "../wizard/WizardFunnel";
import { useFilterEstimate } from "../wizard/useFilterEstimate";
import { submitBatchExport } from "../api/workflows";
import { ApiError, extractErrorMessage } from "../api/client";
import { getMe, MeResponse } from "../api/me";
import { aggregateMonths, getInventory, InventoryRow } from "../api/inventory";

const STEP_LABELS = ["Months", "Filter", "Search", "Output", "Review"];

// The wizard. Walks the user through the five steps, syncs state to
// URL params so a refresh / share-link preserves config, and submits
// a batch_export workflow on the final step.
//
// Two distinct buckets flow through here:
//   - default_archive_bucket → read source for batch_export (sent as
//     the `bucket` param the operator uses to build the per-month
//     index URI: s3://<archive>/YYYY/MM/_index.parquet).
//   - default_output_bucket  → suggested write destination, used to
//     auto-suggest the output prefix the user can edit.
// Both come from /api/me. If archive isn't configured, the wizard
// blocks at submit time with a clear error.

export function ExportNew() {
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [inventory, setInventory] = useState<InventoryRow[] | null>(null);
  const [inventoryError, setInventoryError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();
  // Wizard-level estimate state — owned here so the funnel breadcrumb
  // and the Filter step's Estimate button stay in sync. Estimates only
  // run on explicit user action (button click or Next-from-Filter).
  const filterEstimate = useFilterEstimate();

  // Hydrate from URL on first render so deep-linking works.
  useEffect(() => {
    const fromUrl = searchParamsToState(params);
    if (
      fromUrl.months.length ||
      fromUrl.filter ||
      fromUrl.search ||
      fromUrl.output
    ) {
      dispatch({
        type: "HYDRATE",
        state: { ...initialWizardState, ...fromUrl },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync state → URL on every change.
  useEffect(() => {
    setParams(stateToSearchParams(state), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.months, state.filter, state.search, state.output]);

  // Identity for output prefix templating + bucket fallback.
  useEffect(() => {
    getMe().then(setMe).catch(() => undefined);
  }, []);

  // Inventory is shared between the funnel breadcrumb (always visible)
  // and the Months step. Hoisting the fetch here means a single round-
  // trip per wizard load — both consumers read from the same state.
  useEffect(() => {
    getInventory()
      .then((r) => setInventory(r.inventory))
      .catch((err: unknown) => setInventoryError(extractErrorMessage(err)));
  }, []);

  const advanceStep = () =>
    dispatch({
      type: "GO_TO_STEP",
      step: Math.min(4, state.step + 1) as StepIndex,
    });

  const goNext = () => {
    // Leaving the Filter step with an empty filter against a large
    // input ⇒ pause for explicit acknowledgement. We only gate on
    // (empty filter AND big input) — a filter expression's effect
    // on the output size is unknowable without running the count,
    // so we don't gate filtered exports.
    if (
      state.step === 1 &&
      !state.filter.trim() &&
      inputTotal !== null &&
      inputTotal > LARGE_INPUT_THRESHOLD
    ) {
      setConfirmOpen(true);
      return;
    }
    // Auto-kick the estimate when the user advances out of the Filter
    // step so the funnel/Review have a number ready by the time they
    // arrive — but only if there's something to estimate and it isn't
    // already current.
    if (
      state.step === 1 &&
      state.months.length > 0 &&
      (filterEstimate.state.kind === "idle" ||
        filterEstimate.isStaleFor(state.filter, state.months))
    ) {
      filterEstimate.kick(
        state.filter,
        state.months,
        state.contentTypes,
        estimateInventory,
      );
    }
    advanceStep();
  };
  const goBack = () =>
    dispatch({
      type: "GO_TO_STEP",
      step: Math.max(0, state.step - 1) as StepIndex,
    });

  // The export's read bucket — the operator's indexed archive.
  // Comes from PAIM_DEFAULT_ARCHIVE_BUCKET on the deployment; the
  // wizard never asks for it directly (it's an ops setting, not a
  // user choice). When unset, submission fails fast with a clear
  // message rather than sending an empty `bucket` to the operator.
  const archiveBucket = me?.default_archive_bucket || "";

  // Friction guard for empty-filter exports against a large input.
  // The actual export size depends on the filter, so a Submit-time
  // gate would be guessing; but at the Filter step we know:
  //   (a) no filter is set, AND
  //   (b) the selected months total more than this many pages.
  // That pair is the "you might be about to run an enormous export
  // without realising it" signal — one explicit "I understand"
  // click before leaving the Filter step.
  const LARGE_INPUT_THRESHOLD = 100_000_000;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleSubmit = async () => {
    if (!archiveBucket) {
      setSubmitError(
        "no archive bucket configured — set PAIM_DEFAULT_ARCHIVE_BUCKET on the portal",
      );
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await submitBatchExport({
        bucket: archiveBucket,
        months: state.months,
        filter: state.filter,
        output: state.output,
        output_format: state.outputFormat,
        content_types: state.contentTypes,
        ...(state.search ? { search: state.search } : {}),
        ...(state.description.trim() ? { description: state.description.trim() } : {}),
        ...(state.generateReport ? { generate_report: true } : {}),
      });
      navigate(`/exports/${r.workflow_id}`);
    } catch (err) {
      setSubmitError(
        extractErrorMessage(
          err,
          err instanceof ApiError ? `submit returned ${err.status}` : undefined,
        ),
      );
      setSubmitting(false);
    }
  };

  // Inventory collapsed to one tile per month across the *currently-
  // selected corpora*. The raw inventory has a row per (content_type,
  // year, month); aggregating here keeps the funnel, the empty-filter
  // short-circuit, and the large-input gate all counting the union the
  // export will actually cover. null until inventory loads.
  const monthTiles = useMemo(
    () => (inventory ? aggregateMonths(inventory, state.contentTypes) : null),
    [inventory, state.contentTypes],
  );
  // MonthTile carries year/month/rollup_n_rows, so it satisfies the
  // estimate hook's InventoryRowLite without a mapping step.
  const estimateInventory = monthTiles ?? undefined;

  // Sum rollup_n_rows for the currently-selected months. Used by the
  // Filter-step empty-filter gate; null when inventory hasn't loaded.
  const inputTotal: number | null = (() => {
    if (!monthTiles || state.months.length === 0) return null;
    const idx = new Map(
      monthTiles.map((t) => [`${t.year}-${t.month}`, t.rollup_n_rows]),
    );
    return state.months.reduce((n, m) => n + (idx.get(m) ?? 0), 0);
  })();

  // Import-config button: lets the user pick a previously-exported
  // ``paim-config-*.json`` file and pre-fill the wizard from it.
  // Round-trips via URL search params so the existing on-mount
  // hydration logic (searchParamsToState) handles the population —
  // no separate hydrate path to maintain. Tolerant of both the
  // full ``{params: {…}}`` envelope JobConfigurationPanel writes
  // and a bare params dict, so a user pasting just the relevant
  // bit still works.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const handleImportClick = () => {
    setImportError(null);
    fileInputRef.current?.click();
  };
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";  // allow re-selecting the same file later
    if (!f) return;
    try {
      const text = await f.text();
      const parsed = JSON.parse(text);
      const params = parsed?.params ?? parsed;
      if (!params || typeof params !== "object") {
        throw new Error("file has no params block");
      }
      const sp = new URLSearchParams();
      if (Array.isArray(params.months)) sp.set("months", params.months.join(","));
      if (typeof params.filter === "string") sp.set("filter", params.filter);
      if (typeof params.search === "string") sp.set("search", params.search);
      if (typeof params.output === "string") sp.set("output", params.output);
      if (typeof params.output_format === "string") {
        sp.set("output_format", params.output_format);
      }
      navigate(`/exports/new?${sp.toString()}`, { replace: true });
      // The wizard hydrates from URL params on mount; navigating to
      // the same path with a new query string triggers the effect
      // that calls dispatch({type:"HYDRATE", ...}).
      window.location.reload();
    } catch (err) {
      setImportError(
        err instanceof Error ? err.message : "couldn't parse config file",
      );
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          New dataset
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={handleImportClick}
        >
          Import config…
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          onChange={handleImportFile}
          style={{ display: "none" }}
        />
      </Stack>
      {importError && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="error">
            {importError}
          </Typography>
        </Box>
      )}
      <Stepper activeStep={state.step} sx={{ my: 3 }}>
        {STEP_LABELS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <WizardFunnel
        selectedMonths={state.months}
        inventory={monthTiles}
        filter={state.filter}
        search={state.search}
        step={state.step}
        estimate={filterEstimate.state}
        estimateStale={filterEstimate.isStaleFor(state.filter, state.months)}
      />

      <Box sx={{ minHeight: 200, my: 2 }}>
        {state.step === 0 && (
          <StepMonths
            contentTypes={state.contentTypes}
            onToggleContentType={(ct) =>
              dispatch({ type: "TOGGLE_CONTENT_TYPE", contentType: ct })
            }
            selected={state.months}
            onToggle={(m) => dispatch({ type: "TOGGLE_MONTH", month: m })}
            onSelectAll={(months) => dispatch({ type: "SET_MONTHS", months })}
            inventory={inventory}
            inventoryError={inventoryError}
          />
        )}
        {state.step === 1 && (
          <StepFilter
            contentTypes={state.contentTypes}
            value={state.filter}
            onChange={(filter) => dispatch({ type: "SET_FILTER", filter })}
            estimate={filterEstimate.state}
            estimateStale={filterEstimate.isStaleFor(state.filter, state.months)}
            canEstimate={state.months.length > 0}
            onEstimate={() =>
              filterEstimate.kick(
                state.filter,
                state.months,
                state.contentTypes,
                estimateInventory,
              )
            }
          />
        )}
        {state.step === 2 && (
          <StepSearch
            value={state.search}
            onChange={(search) => dispatch({ type: "SET_SEARCH", search })}
            aiEnabled={me?.features?.ai_query_builder ?? false}
          />
        )}
        {state.step === 3 && (
          <StepOutput
            value={state.output}
            onChange={(output) => dispatch({ type: "SET_OUTPUT", output })}
            outputFormat={state.outputFormat}
            onOutputFormatChange={(outputFormat) =>
              dispatch({ type: "SET_OUTPUT_FORMAT", outputFormat })
            }
            defaultBucket={me?.default_output_bucket ?? null}
            userEmail={me?.email ?? me?.sub ?? null}
          />
        )}
        {state.step === 4 && (
          <StepReview
            state={state}
            bucket={archiveBucket}
            estimate={filterEstimate.state}
            estimateStale={filterEstimate.isStaleFor(state.filter, state.months)}
            costRates={{
              cpuHrUsd: me?.cost_cpu_hr_usd ?? 0.04,
              memGibHrUsd: me?.cost_mem_gib_hr_usd ?? 0.005,
            }}
            aiEnabled={me?.features?.ai_query_builder ?? false}
            onDescriptionChange={(description) =>
              dispatch({ type: "SET_DESCRIPTION", description })
            }
            onGenerateReportChange={(generateReport) =>
              dispatch({ type: "SET_GENERATE_REPORT", generateReport })
            }
            onSubmit={handleSubmit}
            submitting={submitting}
            submitError={submitError}
          />
        )}
      </Box>

      <Stack direction="row" spacing={2}>
        <Button
          onClick={goBack}
          disabled={state.step === 0 || submitting}
        >
          Back
        </Button>
        {state.step < 4 && (
          <Button
            variant="contained"
            onClick={goNext}
            disabled={!canAdvance(state, state.step)}
          >
            Next
          </Button>
        )}
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="confirm-no-filter-title"
      >
        <DialogTitle id="confirm-no-filter-title">
          Proceed without a filter?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            The selected months contain{" "}
            <strong>
              {inputTotal !== null ? inputTotal.toLocaleString() : "—"}
            </strong>{" "}
            pages of source data. Without a filter, every one of them
            enters the export — and the output is the same size again
            (or larger, depending on format). Big datasets are slow to
            produce, consume significant S3 storage, and drive up the
            cost of any search environment opened on them.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Consider going back and narrowing by station, market, or
            program. If you really do want the whole thing, acknowledge
            below.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            Go back and refine
          </Button>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              advanceStep();
            }}
            color="warning"
            variant="contained"
          >
            I understand, proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
